"use client";

import type { QuestCompletion } from "@/components/QuestsV2/types";
import { appConfig } from "@/constants";
import { useAccessToken } from "@/hooks";
import { authorizedGet } from "@/utils/request";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import type {
  GameAPIResponse,
  GetQuestsAPIResponse,
  QuestMetadata,
} from "./types";
import { questCompletedInLatestPeriod } from "./utils";

interface DailyQuest {
  gameName: string;
  slug: string;
  type: string;
  externalId: string;
  validFrom: string;
  refreshPeriodHours: number | null;
  gameId: string;
  gems: number;
  id: number;
  name: string;
  caption: string;
  linkUrl: string | undefined;
  gameIconUrl: string | undefined;
  completedToday: boolean;
  metadata: QuestMetadata | undefined;
}

// Cached fetchers for individual promises
const getQuests = async (): Promise<GetQuestsAPIResponse> => {
  const response = await fetch(
    `${appConfig.IMMUTABLE_BASE_URI}/v1/quests?status=live&category_whitelist=daily_quest`,
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch quests: ${response.status}`);
  }
  return response.json();
};

const getGames = async (): Promise<GameAPIResponse[]> => {
  const response = await fetch(
    `${appConfig.IMMUTABLE_BASE_URI}/v1/quests/games`,
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch games: ${response.status}`);
  }
  return response.json();
};

const getCompletedQuests = async (
  getAccessToken: () => Promise<string | undefined>,
) => {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error("No access token");
  }
  return authorizedGet<{
    result: QuestCompletion[];
  }>("/v1/quests/quest-completions", accessToken);
};

const getDailyQuests = async (
  quests: GetQuestsAPIResponse,
  games: GameAPIResponse[],
  completedQuests: { result: QuestCompletion[] },
) => {
  const completedQuestsMap = new Map<string, QuestCompletion>();
  if (completedQuests.result) {
    for (const completion of completedQuests.result) {
      completedQuestsMap.set(
        `${completion.game_id}-${completion.external_id}`,
        completion,
      );
    }
  }

  const gameMap = new Map(games.map((game) => [game.game_id, game]));
  const dailyQuests = quests.results.map((quest) => {
    const game = gameMap.get(quest.game_id);
    const completion = completedQuestsMap.get(
      `${quest.game_id}-${quest.external_id}`,
    );
    let linkUrl: string | undefined;
    try {
      linkUrl =
        JSON.parse(quest.content_metadata ?? "{}").link_url ?? undefined;
    } catch (error) {}
    let questMetadata: QuestMetadata | undefined;
    try {
      questMetadata = JSON.parse(quest.metadata ?? "{}") as QuestMetadata;
    } catch (error) {
      console.warn(
        `Failed to parse quest metadata for quest ${quest.external_id}: ${error}`,
      );
    }

    let questName = `Play ${quest.game_name}`;
    let caption = "";
    let type = quest.type ?? "";
    if (quest.type === "retweet_and_like") {
      questName = `Repost and like ${quest.game_name} on X`;
    } else if (quest.categories?.includes("social_twitter")) {
      questName = `Tag ${quest.game_name} on X`;
    } else if (quest.categories?.includes("social_discord_engage")) {
      questName = `Engage in ${quest.game_name}'s Discord`;
    } else if (quest.categories?.includes("social_telegram_engage")) {
      questName = `Engage in ${quest.game_name}'s Telegram`;
    } else if (quest.categories?.includes("social_twitter_post")) {
      questName = `Tag ${quest.game_name} on X`;
      type = "post_x_tweet";
      caption = quest.name;
    }

    return {
      gameName: quest.game_name,
      slug: game?.slug ?? "",
      type: type,
      externalId: quest.external_id ?? "",
      gameId: quest.game_id,
      gems: quest.gems,
      id: quest.id,
      validFrom: quest.valid_from ?? "",
      refreshPeriodHours: quest.refresh_period_hours,
      name: questName,
      caption: caption,
      linkUrl: linkUrl ?? `/games/${game?.slug}`,
      gameIconUrl: game?.logo_url ?? undefined,
      completedToday: questCompletedInLatestPeriod(
        quest.valid_from ?? "",
        quest.refresh_period_hours,
        completion,
      ),
      metadata: questMetadata,
    };
  });

  // Sort quests to show social_engagement type first
  return dailyQuests.sort((a, b) => {
    // If both are social_engagement or both are not, maintain original order
    if ((a.type === "social_engagement") === (b.type === "social_engagement")) {
      return 0;
    }
    // social_engagement quests come first
    return a.type === "social_engagement" ? -1 : 1;
  });
};

export const useDailyQuests = () => {
  const getAccessToken = useAccessToken();

  // Individual cached SWR hooks for each promise
  const {
    data: quests,
    isLoading: questsLoading,
    error: questsError,
  } = useSWRImmutable<GetQuestsAPIResponse>("daily-quests-list", getQuests, {
    revalidateOnFocus: false, // Quest list changes less frequently
    revalidateOnReconnect: true,
    dedupingInterval: 10 * 60 * 1000, // 10 minutes deduplication
  });

  const {
    data: games,
    isLoading: gamesLoading,
    error: gamesError,
  } = useSWRImmutable<GameAPIResponse[]>("quest-games-list", getGames, {
    revalidateOnFocus: false, // Games list changes less frequently
    revalidateOnReconnect: true,
    dedupingInterval: 10 * 60 * 1000, // 10 minutes deduplication
  });

  const {
    data: completedQuests,
    isLoading: completedQuestsLoading,
    error: completedQuestsError,
  } = useSWR<{ result: QuestCompletion[] }>(
    ["quest-completions", getAccessToken],
    () => getCompletedQuests(getAccessToken),
    {
      revalidateOnFocus: true, // Quest completions change frequently
      revalidateOnReconnect: true,
      dedupingInterval: 5000, // 5 seconds deduplication to avoid excessive calls
    },
  );

  // Combine the data when all three are available
  const {
    data: combinedData,
    isLoading: combinedLoading,
    error: combinedError,
  } = useSWR<DailyQuest[]>(
    quests && games && completedQuests
      ? ["daily-quests-combined", quests, games, completedQuests]
      : null,
    () => {
      if (!quests || !games || !completedQuests) {
        throw new Error("Missing required data for combining daily quests");
      }
      return getDailyQuests(quests, games, completedQuests);
    },
    {
      revalidateOnFocus: false, // Don't revalidate combined data on focus, rely on completedQuests revalidation
      revalidateOnReconnect: true,
    },
  );

  const isLoading =
    questsLoading || gamesLoading || completedQuestsLoading || combinedLoading;
  const error =
    questsError || gamesError || completedQuestsError || combinedError;

  return {
    data: combinedData ?? [],
    isLoading,
    error,
  };
};

export type { DailyQuest };
